import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'src/componentes/grid';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import InputSearch from 'src/componentes/input-search';
import PanelCollapse from 'src/componentes/panel-collapse';
import StatusBadge from 'src/componentes/StatusBadge';
import Table from 'src/componentes/table';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import { capitalizeFirstLetter } from 'src/utils/string';
import DocumentoAssociadoListLookup from '../EditPage/resumo/DocumentoAssociadoListLookup';
import { CircleX, Search } from 'lucide-react';
import { STATUS_DOCUMENTO } from 'src/utils/constants';

const iconButtonStyle = {
  background: 'none',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  lineHeight: '1px',
  padding: 8,
};

const searchButtonStyle = {
  height: 35,
  marginBottom: 10,
  minHeight: 35,
};

function DocumentosAssociadosPanel({
  documentosAssociados = [],
  onChangeDocumentosAssociados,
  onOpenDocumentoAssociadoDetail,
  documentoAtualId,
  endpointEdit = '/Documento/Edit',
  isDisabled = false,
  panelStyle,
  contentStyle,
  showBuscar = true,
  showSearchBar = true,
}) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [documentosAssociadosCache, setDocumentosAssociadosCache] = useState(
    {}
  );
  const [
    pesquisaRapidaDocumentosAssociados,
    setPesquisaRapidaDocumentosAssociados,
  ] = useState('');

  const getDocumentoAssociadoValue = (item, keys = []) => {
    if (!item) return null;

    const value = keys.find((key) => item?.[key] != null);
    return value ? item[value] : null;
  };

  const formatarDataDocumentoAssociado = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      const valueTrimmed = value.trim();
      if (!valueTrimmed) return null;

      const legacyDateMatch = valueTrimmed.match(
        /\/Date\((\d+)(?:[-+]\d+)?\)\//
      );
      if (legacyDateMatch && legacyDateMatch[1]) {
        const parsedDate = new Date(Number(legacyDateMatch[1]));
        return Number.isNaN(parsedDate.getTime())
          ? valueTrimmed
          : parsedDate.toLocaleDateString('pt-BR');
      }

      return valueTrimmed;
    }

    if (typeof value !== 'string') {
      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime())
        ? null
        : parsedDate.toLocaleDateString('pt-BR');
    }
  };

  const buildDataVigenciaRange = (item) => {
    if (!item) return null;

    const dataInicioVigencia = getDocumentoAssociadoValue(item, [
      'dataInicioVigencia',
    ]);
    const dataTerminoVigencia = getDocumentoAssociadoValue(item, [
      'dataTerminoVigencia',
    ]);

    if (!dataInicioVigencia && !dataTerminoVigencia) return null;

    const dataInicioFormatada =
      formatarDataDocumentoAssociado(dataInicioVigencia);
    const dataTerminoFormatada =
      formatarDataDocumentoAssociado(dataTerminoVigencia);

    return `${dataInicioFormatada || '-'} a ${dataTerminoFormatada || '-'}`;
  };

  const sanitizeDocumentoAssociado = (item) => ({
    id: item?.id,
    codigo: getDocumentoAssociadoValue(item, ['codigo', 'idIntegracao']),
    idIntegracao: getDocumentoAssociadoValue(item, ['idIntegracao']),
    nome: getDocumentoAssociadoValue(item, ['nome', 'documento']),
    tipoDocumento: getDocumentoAssociadoValue(item, ['tipoDocumento']),
    responsaveis: getDocumentoAssociadoValue(item, ['responsaveis']),
    dataVigencia: getDocumentoAssociadoValue(item, ['dataVigencia']),
    status: getDocumentoAssociadoValue(item, ['status']),
    agrupador: getDocumentoAssociadoValue(item, ['agrupador']),
  });

  const normalizeDocumentosAssociados = (itens = []) => {
    return itens.reduce((acc, item) => {
      if (!item?.id || acc.some((current) => current.id === item.id)) {
        return acc;
      }

      acc.push(sanitizeDocumentoAssociado(item));
      return acc;
    }, []);
  };

  const hasInformacoesCompletasDocumentoAssociado = (item) => {
    const tipoDocumento = getDocumentoAssociadoValue(item, ['tipoDocumento']);
    const responsaveis = getDocumentoAssociadoValue(item, ['responsaveis']);
    const dataVigencia = getDocumentoAssociadoValue(item, ['dataVigencia']);
    const status = getDocumentoAssociadoValue(item, ['status']);

    return Boolean(tipoDocumento && responsaveis && dataVigencia && status);
  };

  const mapDocumentoEditToAssociado = (item) => {
    if (!item) return null;

    const itemId = getDocumentoAssociadoValue(item, ['id']);
    if (!itemId) return null;

    const dataVigencia = getDocumentoAssociadoValue(item, ['dataVigencia']);
    const dataVigenciaFormatada =
      formatarDataDocumentoAssociado(dataVigencia) ||
      buildDataVigenciaRange(item);

    return sanitizeDocumentoAssociado({
      id: itemId,
      codigo: getDocumentoAssociadoValue(item, ['codigo', 'idIntegracao']),
      idIntegracao: getDocumentoAssociadoValue(item, ['idIntegracao']),
      nome: getDocumentoAssociadoValue(item, ['nome', 'documento']),
      tipoDocumento: getDocumentoAssociadoValue(item, ['tipoDocumento']),
      responsaveis: getDocumentoAssociadoValue(item, ['responsaveis']),
      dataVigencia: dataVigenciaFormatada,
      status: getDocumentoAssociadoValue(item, ['status']),
      agrupador: getDocumentoAssociadoValue(item, ['agrupador']),
    });
  };

  const formatTipoDocumentoAssociado = (tipoDocumento) => {
    if (!tipoDocumento) return '-';
    if (typeof tipoDocumento === 'string') return tipoDocumento;
    return tipoDocumento?.nome || tipoDocumento?.descricao || '-';
  };

  const formatResponsaveisDocumentoAssociado = (responsaveis) => {
    if (!responsaveis) return '-';

    if (Array.isArray(responsaveis)) {
      return responsaveis
        .map((responsavel) => responsavel?.nome || responsavel)
        .filter(Boolean)
        .join(', ');
    }

    return responsaveis;
  };

  const renderStatusDocumentoAssociado = (status) => {
    if (!status) return '-';
    if (typeof status === 'string') return status;

    const nomeStatus = status?.nome || status?.descricao || '-';
    const corStatusTexto = status?.cor;
    const corStatusFundo = status?.corFundo || corStatusTexto;

    if (!corStatusFundo) return nomeStatus;
    if (
      status?.id !== STATUS_DOCUMENTO.VIGENTE &&
      status?.id !== STATUS_DOCUMENTO.VENCIDO
    )
      return '-';

    return (
      <StatusBadge
        text={nomeStatus}
        textColor={status?.corFundo ? corStatusTexto : undefined}
        backgroundColor={corStatusFundo}
      />
    );
  };

  const getDocumentoAssociadoDisplay = (item) => {
    const itemId = getDocumentoAssociadoValue(item, ['id']);
    const itemDetalhado = itemId ? documentosAssociadosCache[itemId] : null;

    const codigo =
      getDocumentoAssociadoValue(item, ['codigo', 'idIntegracao']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['codigo', 'idIntegracao']);
    const tipoDocumento =
      getDocumentoAssociadoValue(item, ['tipoDocumento']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['tipoDocumento']);
    const nome =
      getDocumentoAssociadoValue(item, ['nome', 'documento']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['nome', 'documento']);
    const responsaveis =
      getDocumentoAssociadoValue(item, ['responsaveis']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['responsaveis']);
    const status =
      getDocumentoAssociadoValue(item, ['status']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['status']);
    const dataVigenciaDireta =
      getDocumentoAssociadoValue(item, ['dataVigencia']) ||
      getDocumentoAssociadoValue(itemDetalhado, ['dataVigencia']);
    const dataVigencia =
      formatarDataDocumentoAssociado(dataVigenciaDireta) ||
      buildDataVigenciaRange(item) ||
      buildDataVigenciaRange(itemDetalhado);

    return {
      itemId,
      codigo: codigo || '-',
      tipoDocumento: formatTipoDocumentoAssociado(tipoDocumento),
      nome: nome || '-',
      responsaveis: formatResponsaveisDocumentoAssociado(responsaveis),
      dataVigencia: dataVigencia || '-',
      status,
    };
  };

  const handleChange = (itens = []) => {
    onChangeDocumentosAssociados?.(normalizeDocumentosAssociados(itens));
  };

  const handleOpenDocumentosAssociadosLookup = () => {
    if (isDisabled) return;

    ContentManager.addContent(
      <DocumentoAssociadoListLookup
        title={capitalizeFirstLetter(
          intl.formatMessage({
            id: 'documentos',
          })
        )}
        multiple
        select
        selectedCallback={handleChange}
        selectedIds={documentosAssociados}
        selectedItems={documentosAssociados}
        parameters={{ documentoAtualId }}
      />
    );
  };

  const handleRemoveDocumentoAssociado = (itemId) => {
    handleChange(
      documentosAssociados.filter((itemDocumentoAssociado) => {
        return itemDocumentoAssociado?.id !== itemId;
      })
    );
  };

  const handleOpenDocumentoAssociadoDetail = (item) => {
    onOpenDocumentoAssociadoDetail?.(item);
  };

  const documentosAssociadosFiltrados = (documentosAssociados || []).filter(
    (item) => {
      const searchText = (pesquisaRapidaDocumentosAssociados || '').trim();
      if (!searchText) return true;

      const itemDisplay = getDocumentoAssociadoDisplay(item);
      const searchSource = [itemDisplay.codigo, itemDisplay.nome]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchSource.includes(searchText.toLowerCase());
    }
  );

  useEffect(() => {
    const itensParaCarregar = (documentosAssociados || []).filter((item) => {
      const itemId = getDocumentoAssociadoValue(item, ['id']);

      if (!itemId) return false;
      if (documentosAssociadosCache[itemId]) return false;

      return !hasInformacoesCompletasDocumentoAssociado(item);
    });

    if (!itensParaCarregar.length) return;

    const carregarDetalhes = async () => {
      try {
        const responses = await Promise.all(
          itensParaCarregar.map((item) =>
            http.post(endpointEdit, {
              id: getDocumentoAssociadoValue(item, ['id']),
              parameters: {},
            })
          )
        );

        const newCache = {};

        responses.forEach((response) => {
          const mappedItem = mapDocumentoEditToAssociado(response?.data);
          if (mappedItem?.id) {
            newCache[mappedItem.id] = mappedItem;
          }
        });

        if (Object.keys(newCache).length) {
          setDocumentosAssociadosCache((prevState) => ({
            ...prevState,
            ...newCache,
          }));
        }
      } catch (err) {
        errorHandler(err);
      }
    };

    carregarDetalhes();
  }, [documentosAssociados, documentosAssociadosCache, endpointEdit]);

  return (
    <PanelCollapse
      open={true}
      style={panelStyle}
      header={intl.formatMessage({
        id: 'labelDocumentosAssociados',
      })}
    >
      <div style={contentStyle}>
        <Row>
          <Col md={5} lg={4}>
            {showSearchBar && (
              <InputSearch
                placeholder={intl.formatMessage({
                  id: 'label.pesquisaRapida',
                })}
                value={pesquisaRapidaDocumentosAssociados}
                onChange={(event) =>
                  setPesquisaRapidaDocumentosAssociados(event?.target?.value)
                }
              />
            )}
          </Col>
          <Col md={7} lg={8}>
            {showBuscar && (
              <Button
                className="pull-right"
                leftIcon={Search}
                type="primary"
                style={searchButtonStyle}
                onClick={handleOpenDocumentosAssociadosLookup}
                disabled={isDisabled}
              >
                {intl.formatMessage({ id: 'label.buscar' })}
              </Button>
            )}
          </Col>
        </Row>
        {documentosAssociadosFiltrados.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'codigo' })}</th>
                <th>{intl.formatMessage({ id: 'labelTipoDocumento' })}</th>
                <th>{intl.formatMessage({ id: 'documento' })}</th>
                <th>{capitalizeFirstLetter(resources.responsaveis)}</th>
                <th>{intl.formatMessage({ id: 'dataVigencia' })}</th>
                <th>{intl.formatMessage({ id: 'labelSituacao' })}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documentosAssociadosFiltrados.map((item, index) => {
                const itemDisplay = getDocumentoAssociadoDisplay(item);
                const {
                  itemId,
                  codigo,
                  tipoDocumento,
                  nome,
                  responsaveis,
                  dataVigencia,
                  status,
                } = itemDisplay;

                return (
                  <tr key={`${itemId || index}-documento-associado`}>
                    <td>{codigo}</td>
                    <td>{tipoDocumento}</td>
                    <td>
                      {itemId ? (
                        <span
                          className="font-weight-bold text-primary cursor-pointer"
                          onClick={() =>
                            handleOpenDocumentoAssociadoDetail(item)
                          }
                        >
                          {nome || '-'}
                        </span>
                      ) : (
                        nome || '-'
                      )}
                    </td>
                    <td>{responsaveis}</td>
                    <td>{dataVigencia}</td>
                    <td>{renderStatusDocumentoAssociado(status)}</td>
                    <td style={{ width: 40 }}>
                      {!isDisabled && (
                        <button
                          type="button"
                          className="clarear"
                          style={iconButtonStyle}
                          onClick={() => handleRemoveDocumentoAssociado(itemId)}
                        >
                          <CircleX size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <i>{intl.formatMessage({ id: 'label.naoHaItensRelacionados' })}</i>
        )}
      </div>
    </PanelCollapse>
  );
}

export default DocumentosAssociadosPanel;
